import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from 'src/hooks/use-set-state';
import { useRouter, useSearchParams, usePathname } from 'src/routes/hooks';
import axios, { endpoints } from 'src/utils/axios';
import { API_CONFIG } from 'src/config/api';

import { STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const router = useRouter();
  const pathname = usePathname();

  const { state, setState } = useSetState({
    user: null,
    client: null,
    professional: null,
    establishment: {},
    loading: true,
    services: [],
    have_plan: false,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken) {
        if (localStorage.getItem("lindu_client")) {
          setSession(accessToken);
          const res = await fetch(`${API_CONFIG.BASE_URL}/api/cliente/self`, {
            method: "GET",
            headers: {
              "Authorization": accessToken
            }
          })
          const data = await res.json();
          setState({ client: { ...data, accessToken }, loading: false });
          setState({ user: { ...data, accessToken }, loading: false });

          setState({ establishment: null });

          if (pathname.includes('/auth')) {
            router.replace('/dashboard/calendar-client');
          }
        } else {
          setSession(accessToken);

          // Tenta profissional primeiro
          const profRes = await fetch(`${API_CONFIG.BASE_URL}/api/profissionais-estabelecimento/self`, {
            method: "GET",
            headers: { "Authorization": accessToken }
          });

          if (profRes.ok) {
            const data = await profRes.json();
            setState({ professional: { ...data, accessToken }, user: { ...data, accessToken }, establishment: null, loading: false });
            if (pathname.includes('/auth')) {
              router.replace('/dashboard/calendar');
            }
          } else {
            // Senão, é estabelecimento
            const estabRes = await fetch(`${API_CONFIG.BASE_URL}/api/estabelecimento/self`, {
              method: "GET",
              headers: { "Authorization": accessToken }
            });
            const data = await estabRes.json();
            setState({ establishment: data, have_plan: !!data.paymentStatus });
            setState({ user: { ...data, accessToken }, loading: false });
          }
        }

      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState, router, pathname]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addService = useCallback(async (service) => {
    try {
      setState({ services: [...state.services, service] });
    } catch (error) {
      console.error(error);
    }
  }, [state.services, setState]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
          ...state.user,
          role: state.user?.role ?? 'admin',
        }
        : null,
      client: state.client,
      professional: state.professional,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      establishment: state.establishment,
      have_plan: state.have_plan,
      addService,
    }),
    [checkUserSession, addService, state.user, status, state.establishment, state.client, state.professional, state.have_plan]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}